import React, {useEffect, useRef, useState} from 'react'
import {
  AdyenCheckout,
  Dropin,
  Card,
  GooglePay,
  ApplePay,
  UIElement,
  AdyenCheckoutError
} from '@adyen/adyen-web'
import '@adyen/adyen-web/styles/adyen.css'
import type {
  PaymentFailedData,
  PaymentCompletedData,
  CoreConfiguration,
  DropinConfiguration,
  BeforeSubmitActions,
  PaymentData
} from '@adyen/adyen-web'
import {getConfig} from '../../services/config'
import {getLogger} from '../../services/logging'
import Alert from '../common/Alert'
import {PaymentSession} from '../../schema'
import useCartValidator from '../../hooks/useCartValidator'

const logger = getLogger('CheckoutPaymentForm')
const {adyenClientId, adyenEnvironment} = getConfig()

interface CheckoutPaymentFormProps {
  paymentMethods: any
  paymentSession: PaymentSession
  onPaymentCompleted: (result: any) => void
  onPaymentFailed: (result: any) => void
}
type AdyenEnvironment =
  | 'test'
  | 'live'
  | 'live-us'
  | 'live-au'
  | 'live-apse'
  | 'live-in'

export default function CheckoutPaymentForm({
  paymentMethods,
  paymentSession,
  onPaymentCompleted,
  onPaymentFailed
}: CheckoutPaymentFormProps) {
  const dropinContainerRef = useRef<any>(null)
  const dropinComponentRef = useRef<Dropin | null>(null)
  const isInitialized = useRef<boolean>(false)
  const [hasError, setHasError] = useState<boolean>(false)
  const {validate} = useCartValidator()
  const preferredOrder = ['scheme', 'applepay', 'googlepay']
  const allowedPaymentMethods = {
    paymentMethods: paymentMethods.paymentMethods
      .filter((pm: any) => preferredOrder.includes(pm.type))
      .sort((a: any, b: any) => {
        return preferredOrder.indexOf(a.type) - preferredOrder.indexOf(b.type)
      })
  }

  const initializePaymentForm = async () => {
    logger.info({
      message: 'Initializing adyen payment form...',
      data: {adyenClientId, adyenEnvironment, paymentMethods, paymentSession}
    })

    const checkoutConfig: CoreConfiguration = {
      clientKey: adyenClientId,
      environment: adyenEnvironment as AdyenEnvironment,
      locale: 'en-US',
      paymentMethodsResponse: allowedPaymentMethods,
      session: {
        id: paymentSession?.id,
        sessionData: paymentSession?.sessionData
      },
      // see: https://github.com/Adyen/adyen-web/blob/main/packages/server/translations/en-US.json
      translations: {
        'en-US': {
          'confirmPurchase': 'Pay Now',
          'confirmPreauthorization': 'Pay Later',
          'payButton.saveDetails': 'Pay Later',
          'resultMessages.preauthorized': 'Payment info submitted',
          'creditCard.success': 'Payment submitted'
        }
      },
      onPaymentCompleted(data: PaymentCompletedData, _element: UIElement) {
        logger.info({
          message: 'Payment accepted and being processed.',
          data: {data, paymentSession, paymentMethods}
        })
        if (onPaymentCompleted) {
          if (data.resultCode === 'Authorised') {
            if (onPaymentCompleted) {
              onPaymentCompleted(data)
            }
          } else {
            // set error state
            setHasError(true)

            // scroll to top of window
            if (window) {
              window.scrollTo(0, 0)
            }
          }
        }
      },
      onPaymentFailed(data: PaymentFailedData, _element: UIElement) {
        logger.error({
          message: 'Payment failed',
          data: {data, paymentSession, paymentMethods}
        })

        // set error state
        setHasError(true)

        // scroll to top of window
        if (window) {
          window.scrollTo(0, 0)
        }

        if (onPaymentFailed) {
          onPaymentFailed(data)
        }
      },
      beforeSubmit(
        data: PaymentData,
        component: UIElement,
        actions: BeforeSubmitActions
      ) {
        logger.info({
          message: 'Final cart validation before submitting payment...',
          data: {data, component, paymentSession, paymentMethods}
        })

        // validate the cart before submitting payment
        validate().then(isValid => {
          if (isValid) {
            logger.info({
              message: 'Cart validation succeeded, submitting payment...',
              data: {data, component, paymentSession, paymentMethods}
            })
            actions.resolve(data)
          } else {
            logger.error({
              message: 'Cart validation failed, rejecting payment submission.',
              data: {data, component, paymentSession, paymentMethods}
            })
            actions.reject()
            component.setStatus('error')
          }
        })
      },
      onError(error: AdyenCheckoutError) {
        logger.error({
          message: 'Payment form error',
          data: {error, paymentSession, paymentMethods}
        })
      }
    }

    logger.info({
      message: 'Initializing adyen checkout component...',
      data: {checkoutConfig}
    })

    const checkout = await AdyenCheckout(checkoutConfig)

    const dropinConfig: DropinConfiguration = {
      paymentMethodsConfiguration: {
        card: {
          hasHolderName: true,
          holderNameRequired: true,
          billingAddressRequired: true,
          data: {
            billingAddress: {
              country: 'US'
            }
          }
        }
      },
      paymentMethodComponents: [Card, GooglePay, ApplePay],
      instantPaymentTypes: ['applepay', 'googlepay']
    }

    logger.info({
      message: 'Initializing adyen dropin component...',
      data: {dropinConfig}
    })

    const dropin = new Dropin(checkout, dropinConfig).mount(
      dropinContainerRef.current
    )
    dropinComponentRef.current = dropin

    logger.info({
      message: 'Adyen payment form initialized',
      data: {dropin}
    })
  }

  useEffect(() => {
    logger.info({
      message: 'Initializing adyen dropin component...',
      data: {paymentMethods, paymentSession}
    })

    if (
      dropinContainerRef.current &&
      paymentMethods &&
      paymentSession &&
      !isInitialized.current
    ) {
      isInitialized.current = true
      initializePaymentForm()
    }

    return () => {
      if (dropinComponentRef.current) {
        logger.info({
          message: 'Destroying adyen dropin component...'
        })
        dropinComponentRef.current.remove()
        dropinComponentRef.current = null
      }
    }
  }, [dropinContainerRef.current, paymentMethods, paymentSession])

  return (
    <div className="flex flex-col gap-4">
      {hasError && (
        <Alert type="error">
          <p className="text-left">
            There was an error processing your payment. Please make sure your
            payment information is correct and try again. If the problem
            persists, please contact support:{' '}
            <a
              className="link"
              target="_blank"
              href="mailto:hello@placemakr.com"
              aria-label="Email the Placemakr support team."
            >
              hello@placemakr.com
            </a>
          </p>
        </Alert>
      )}
      <div ref={dropinContainerRef} id="dropin-container"></div>
    </div>
  )
}
